import React, { useState, useEffect } from 'react'
import {
  Card, Table, Button, Tag, Input, Select, Tabs, Space,
  Progress, Row, Col, Tooltip, message,
} from 'antd'
import {
  PlusOutlined, SearchOutlined, EyeOutlined, PrinterOutlined,
  CheckCircleOutlined, ClockCircleOutlined, MailOutlined, FilePdfOutlined,
  PercentageOutlined, RiseOutlined,
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { PaymentInvoiceApplication, PaymentRecord, PaymentStatus, paymentStore } from './paymentStore'
import { invoiceStore } from '@/pages/Invoice/invoiceStore'
import { printReceipt, downloadReceiptAsPDF, emailReceipt } from '@/utils/receiptGenerator'
import { useModulePermission } from '@/hooks/useModulePermission'
import { ApiError } from '@/services/api'
import { WorkflowChainDisplay } from '@/components/WorkflowChainDisplay'
import TableSkeleton from '@/components/TableSkeleton'
import KpiCardRow from '@/components/KpiCardRow'
import { formatIndianCompact, formatIndianFull } from '@/utils/numberFormat'

interface OutstandingRow {
  customerId: string
  customer: string
  invoiceCount: number
  totalBilled: number
  totalReceived: number
  lastPayment: string
  mode?: string
  dueDate: string
  tds?: number
}

const modeColor: Record<string, string> = {
  NEFT: '#2563EB', RTGS: '#7C3AED', UPI: '#059669', Cheque: '#92400E', Cash: '#4A5568', IMPS: '#0891B2',
}

const statusCfg: Record<string, { label: string; color: string; bg: string }> = {
  confirmed: { label: 'Confirmed', color: '#065F46', bg: '#D1FAE5' },
  pending:   { label: 'Pending',   color: '#92400E', bg: '#FEF3C7' },
  bounced:   { label: 'Bounced',   color: '#991B1B', bg: '#FEE2E2' },
}

// Collection status pills — Unpaid · Partially Paid · Paid · TDS Deducted · Overdue
const paymentStatusCfg: Record<PaymentStatus, { label: string; color: string; bg: string }> = {
  unpaid:         { label: 'Unpaid',         color: '#6B7280', bg: '#F3F4F6' },
  partially_paid: { label: 'Partially Paid', color: '#92400E', bg: '#FEF3C7' },
  paid:           { label: 'Paid',           color: '#065F46', bg: '#D1FAE5' },
  tds_deducted:   { label: 'TDS Deducted',   color: '#5B21B6', bg: '#EDE9FE' },
  overdue:        { label: 'Overdue',        color: '#991B1B', bg: '#FEE2E2' },
}

const PaymentStatusPill = ({ status }: { status?: PaymentStatus }) => {
  const c = paymentStatusCfg[status ?? 'unpaid']
  return <span className="pill" style={{ background: c.bg, color: c.color }}>{c.label}</span>
}

/** Collection status for an outstanding customer row. */
const deriveOutstandingStatus = (r: OutstandingRow): PaymentStatus => {
  const balance = r.totalBilled - r.totalReceived
  if (balance <= 0) return 'paid'
  if (r.tds && balance <= r.tds) return 'tds_deducted'
  const overdue = r.dueDate && new Date(r.dueDate) < new Date()
  if (overdue) return 'overdue'
  return r.totalReceived > 0 ? 'partially_paid' : 'unpaid'
}

const fmt  = (n: number) => `₹${n.toLocaleString('en-IN')}`

const pModeMatch = (filterVal: string, status: PaymentStatus, mode?: string) => {
  if (!filterVal) return true
  if (filterVal === status) return true
  if (mode && mode === filterVal) return true
  return false
}

const PaymentList: React.FC = () => {
  const navigate = useNavigate()
  const { canEmail, canDownload } = useModulePermission('Finance')
  const [search, setSearch] = useState('')
  const [modeFilter, setModeFilter] = useState('')
  const [payments, setPayments] = useState<PaymentRecord[]>(() => paymentStore.getAll())
  const [emailingId, setEmailingId] = useState<string | null>(null)
  const [downloadingId, setDownloadingId] = useState<string | null>(null)

  useEffect(() => {
    Promise.all([paymentStore.refresh(), invoiceStore.refresh()]).then(() => setPayments(paymentStore.getAll()))
  }, [])

  // ── Customer Outstanding — derived from real consumer invoices + payments ──
  const lastPayByCustomer: Record<string, string> = {}
  const lastPayModeByCustomer: Record<string, string> = {}
  payments.forEach(p => {
    if (!lastPayByCustomer[p.customerId] || p.date > lastPayByCustomer[p.customerId]) {
      lastPayByCustomer[p.customerId] = p.date
      lastPayModeByCustomer[p.customerId] = p.mode
    }
  })
  // Item 28: Exclude rejected & cancelled invoices from Customer Outstanding
  const activeConsumerInvoices = invoiceStore.getAll().filter(i =>
    i.type === 'consumer' &&
    i.status !== 'rejected' &&
    i.status !== 'cancelled' &&
    (i.status as any) !== 'draft'
  )

  const outstandingRows: OutstandingRow[] = Object.values(
    activeConsumerInvoices.reduce((acc, inv) => {
      const key = inv.entityId || inv.entity
      if (!acc[key]) acc[key] = {
        customerId: inv.entityId || '', customer: inv.entity, invoiceCount: 0,
        totalBilled: 0, totalReceived: 0, lastPayment: '—', dueDate: inv.dueDate || '', tds: 0,
      }
      acc[key].invoiceCount += 1
      acc[key].totalBilled += Number(inv.totalAmount) || 0
      acc[key].totalReceived += Number(inv.paidAmount) || 0
      if (inv.dueDate && (!acc[key].dueDate || inv.dueDate < acc[key].dueDate)) acc[key].dueDate = inv.dueDate
      return acc
    }, {} as Record<string, OutstandingRow>)
  ).map(r => ({ ...r, lastPayment: lastPayByCustomer[r.customerId] || '—', mode: lastPayModeByCustomer[r.customerId] }))

  // Item 25: Correct Total Collected, Total Outstanding, and Collection Rate
  const totalCollected   = payments.filter(p => p.status === 'confirmed' || (p.status as any) === 'Paid' || !p.status).reduce((s, p) => s + Number(p.amount), 0)
  const totalTds         = payments.reduce((s, p) => s + Number(p.tds || 0), 0)
  const totalOutstanding = outstandingRows.reduce((s, o) => s + Math.max(0, Number(o.totalBilled) - Number(o.totalReceived)), 0)
  const overdueCount     = outstandingRows.filter(o => o.totalReceived < o.totalBilled).length
  const totalReceivable  = totalCollected + totalOutstanding
  const collectionRate   = totalReceivable > 0 ? Math.round((totalCollected / totalReceivable) * 100) : 0

  const kpiCards = [
    {
      label: 'Total Collected',
      value: <Tooltip title={formatIndianFull(totalCollected, { isCurrency: true })}>{formatIndianCompact(totalCollected, { isCurrency: true })}</Tooltip>,
      icon: <CheckCircleOutlined style={{ fontSize: 24, color: 'var(--brand-green)' }} />,
      iconBg: 'rgba(5,150,105,0.08)',
      borderColor: 'var(--brand-green)',
    },
    {
      label: 'Outstanding',
      value: <Tooltip title={formatIndianFull(totalOutstanding, { isCurrency: true })}>{formatIndianCompact(totalOutstanding, { isCurrency: true })}</Tooltip>,
      icon: <ClockCircleOutlined style={{ fontSize: 24, color: 'var(--brand-energy)' }} />,
      iconBg: 'rgba(245,158,11,0.08)',
      borderColor: 'var(--brand-energy)',
    },
    {
      label: 'TDS Deducted',
      value: <Tooltip title={formatIndianFull(totalTds, { isCurrency: true })}>{formatIndianCompact(totalTds, { isCurrency: true })}</Tooltip>,
      icon: <PercentageOutlined style={{ fontSize: 24, color: '#7C3AED' }} />,
      iconBg: 'rgba(124,58,237,0.08)',
      borderColor: '#7C3AED',
    },
    {
      label: 'Collection Rate',
      value: `${collectionRate}%`,
      icon: <RiseOutlined style={{ fontSize: 24, color: 'var(--brand-primary)' }} />,
      iconBg: 'rgba(0,26,77,0.08)',
      borderColor: 'var(--brand-primary)',
    },
  ]

  const filteredPayments = payments.filter(p =>
    (!search || p.customer.toLowerCase().includes(search.toLowerCase()) || p.receiptNumber.toLowerCase().includes(search.toLowerCase())) &&
    (!modeFilter || p.mode === modeFilter)
  )

  // Item 27: Customer Outstanding search and status filter
  const filteredOutstandingRows = outstandingRows.filter(r => {
    const matchSearch = !search ||
      r.customer.toLowerCase().includes(search.toLowerCase()) ||
      r.customerId.toLowerCase().includes(search.toLowerCase())
    const status = deriveOutstandingStatus(r)
    const matchMode = !modeFilter || pModeMatch(modeFilter, status, r.mode)
    return matchSearch && matchMode
  })

  const paymentColumns = [
    {
      title: 'Receipt #',
      key: 'receipt',
      render: (_: any, r: PaymentRecord) => (
        <div>
          <div style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, fontSize: 13, color: '#001A4D' }}>{r.receiptNumber}</div>
          <div style={{ fontSize: 11, color: '#8A93A6', fontFamily: 'JetBrains Mono, monospace' }}>{r.date}</div>
        </div>
      ),
    },
    {
      title: 'Customer',
      key: 'customer',
      render: (_: any, r: PaymentRecord) => (
        <div>
          <div style={{ fontWeight: 600, fontSize: 13 }}>{r.customer}</div>
          <div style={{ fontSize: 11, color: '#8A93A6' }}>{r.customerId}</div>
        </div>
      ),
    },
    {
      title: 'Invoices',
      dataIndex: 'invoices',
      render: (invs: PaymentInvoiceApplication[]) => (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {(invs || []).map(i => (
            <Tag key={i.invoiceId} style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, borderRadius: 4, margin: 0 }}>{i.invoiceNumber}</Tag>
          ))}
        </div>
      ),
    },
    {
      title: 'Amount',
      key: 'amount',
      render: (_: any, r: PaymentRecord) => (
        <div>
          <div style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, fontSize: 14, color: '#059669' }}>{fmt(r.amount)}</div>
          {r.tds > 0 && <div style={{ fontSize: 11, color: '#92400E' }}>TDS: {fmt(r.tds)}</div>}
        </div>
      ),
    },
    {
      title: 'Mode',
      dataIndex: 'mode',
      render: (m: string) => (
        <Tag style={{ background: modeColor[m] + '18', color: modeColor[m], border: 'none', borderRadius: 100, fontWeight: 600, fontSize: 11 }}>{m}</Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (s: string) => {
        const c = statusCfg[s]
        return <span className="pill" style={{ background: c.bg, color: c.color }}>{c.label}</span>
      },
    },
    {
      title: 'Payment Status',
      key: 'paymentStatus',
      render: (_: any, r: PaymentRecord) => <PaymentStatusPill status={r.paymentStatus} />,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, r: PaymentRecord) => (
        <Space size={4} onClick={(e) => e.stopPropagation()}>
          <Button size="small" icon={<EyeOutlined />} onClick={() => navigate(`/payments/${r.id}`)}>View</Button>
          {canEmail && (
            <Tooltip title="Email Receipt">
              <Button
                size="small"
                icon={<MailOutlined />}
                loading={emailingId === r.id}
                onClick={async () => {
                  setEmailingId(r.id)
                  try {
                    const sentTo = await emailReceipt(r)
                    message.success(`Receipt emailed to ${sentTo}`)
                  } catch (err) {
                    message.error(err instanceof ApiError ? err.message : 'Could not send the email. Please try again.')
                  } finally {
                    setEmailingId(null)
                  }
                }}
              />
            </Tooltip>
          )}
          {canDownload && (
            <Tooltip title="Download Receipt">
              <Button
                size="small"
                icon={<FilePdfOutlined />}
                loading={downloadingId === r.id}
                onClick={async () => {
                  setDownloadingId(r.id)
                  try {
                    await downloadReceiptAsPDF(r)
                    message.success('Receipt PDF downloaded successfully!')
                  } catch (err) {
                    message.error(err instanceof ApiError ? err.message : 'Failed to generate PDF download. Please try again.')
                  } finally {
                    setDownloadingId(null)
                  }
                }}
              />
            </Tooltip>
          )}
          <Tooltip title="Print Receipt">
            <Button size="small" icon={<PrinterOutlined />} onClick={() => printReceipt(r)} />
          </Tooltip>
        </Space>
      ),
    },
  ]

  const outstandingColumns = [
    {
      title: 'Customer',
      key: 'customer',
      render: (_: any, r: OutstandingRow) => (
        <div>
          <div style={{ fontWeight: 700, fontSize: 13 }}>{r.customer}</div>
          <div style={{ fontSize: 11, color: '#8A93A6' }}>{r.customerId}</div>
        </div>
      ),
    },
    {
      title: 'Invoices',
      dataIndex: 'invoiceCount',
      render: (n: number) => (
        <span style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700 }}>{n}</span>
      ),
    },
    {
      title: 'Total Billed',
      dataIndex: 'totalBilled',
      render: (v: number) => <span style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700 }}>{fmt(v)}</span>,
    },
    {
      title: 'Received',
      dataIndex: 'totalReceived',
      render: (v: number) => <span style={{ fontFamily: 'JetBrains Mono, monospace', color: '#059669', fontWeight: 700 }}>{fmt(v)}</span>,
    },
    {
      title: 'Outstanding',
      key: 'outstanding',
      render: (_: any, r: OutstandingRow) => {
        const pending = r.totalBilled - r.totalReceived
        const pct = Math.round((r.totalReceived / r.totalBilled) * 100)
        return (
          <div style={{ minWidth: 140 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 3 }}>
              <span style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, color: pending > 0 ? '#DC2626' : '#059669' }}>
                {pending > 0 ? fmt(pending) : '—'}
              </span>
              <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: '#8A93A6' }}>{pct}% paid</span>
            </div>
            <Progress percent={pct} showInfo={false} strokeColor={pct === 100 ? '#059669' : '#F59E0B'} trailColor="#E4E8F0" size="small" style={{ margin: 0 }} />
          </div>
        )
      },
    },
    {
      title: 'Payment Status',
      key: 'paymentStatus',
      render: (_: any, r: OutstandingRow) => <PaymentStatusPill status={deriveOutstandingStatus(r)} />,
    },
    {
      title: 'Last Payment',
      dataIndex: 'lastPayment',
      render: (d: string) => <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12, color: '#8A93A6' }}>{d}</span>,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, r: OutstandingRow) => (
        <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => navigate('/payments/new', { state: { customerId: r.customerId } })}>
          Record Payment
        </Button>
      ),
    },
  ]

  const tabItems = [
    {
      key: 'payments',
      label: `Payment Records (${payments.length})`,
      children: (
        <Table
          dataSource={filteredPayments}
          columns={paymentColumns}
          rowKey="id"
          pagination={{ defaultPageSize: 10, pageSizeOptions: ['10', '25', '50', '100'], showSizeChanger: true, showTotal: t => `${t} records` }}
          onRow={(record) => ({
            onClick: () => navigate(`/payments/${record.id}`),
            style: { cursor: 'pointer' },
          })}
        />
      ),
    },
    {
      key: 'outstanding',
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <ClockCircleOutlined style={{ color: overdueCount > 0 ? '#F59E0B' : undefined }} />
          Customer Outstanding ({overdueCount} pending)
        </span>
      ),
      children: (
        <div>
          <div style={{ padding: '12px 0 16px', display: 'flex', gap: 24 }}>
            <div style={{ fontSize: 12, color: '#8A93A6' }}>
              Total outstanding across all customers:
              <span style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, color: '#DC2626', marginLeft: 8, fontSize: 14 }}>
                {fmt(totalOutstanding)}
              </span>
            </div>
          </div>
          <Table
            dataSource={filteredOutstandingRows}
            columns={outstandingColumns}
            rowKey="customerId"
            pagination={false}
            summary={() => (
              <Table.Summary.Row style={{ background: '#F1F4F9' }}>
                <Table.Summary.Cell index={0}><strong>Total</strong></Table.Summary.Cell>
                <Table.Summary.Cell index={1}>
                  <span style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700 }}>
                    {outstandingRows.reduce((s, o) => s + Number(o.invoiceCount), 0)}
                  </span>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2}>
                  <span style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700 }}>
                    {fmt(outstandingRows.reduce((s, o) => s + Number(o.totalBilled), 0))}
                  </span>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3}>
                  <span style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, color: '#059669' }}>
                    {fmt(outstandingRows.reduce((s, o) => s + Number(o.totalReceived), 0))}
                  </span>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={4}>
                  <span style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, color: '#DC2626' }}>
                    {fmt(totalOutstanding)}
                  </span>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={5} />
                <Table.Summary.Cell index={6} />
                <Table.Summary.Cell index={7} />
              </Table.Summary.Row>
            )}
          />
        </div>
      ),
    },
  ]

  if (!paymentStore.loaded || !invoiceStore.loaded) return <TableSkeleton columns={8} />

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h2 className="page-title">Payments & Collections</h2>
          <p className="page-subtitle">Record receipts, track outstanding, manage TDS and multi-invoice allocation</p>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/payments/new')}>
          Record Payment
        </Button>
      </div>

      {/* KPI Cards */}
      <KpiCardRow cards={kpiCards} />

      <WorkflowChainDisplay moduleName="Payment" />

      {/* Tabs card */}
      <Card
        styles={{ body: { padding: 0 } }}
        extra={
          <Space>
            <Input
              placeholder="Search receipt / customer..."
              prefix={<SearchOutlined style={{ color: '#8A93A6' }} />}
              style={{ width: 240 }}
              value={search}
              onChange={e => setSearch(e.target.value)}
              allowClear
            />
            <Select
              placeholder="Payment Mode"
              allowClear
              style={{ width: 140 }}
              value={modeFilter || undefined}
              onChange={v => setModeFilter(v ?? '')}
              options={['NEFT', 'RTGS', 'UPI', 'Cheque', 'Cash', 'IMPS'].map(m => ({ label: m, value: m }))}
            />
          </Space>
        }
      >
        <Tabs items={tabItems} style={{ padding: '0 20px' }} />
      </Card>

      <div style={{ marginTop: 12, fontSize: 12, color: '#8A93A6' }}>
        <CheckCircleOutlined style={{ marginRight: 4, color: '#059669' }} />
        PY-04: One receipt can be applied across multiple invoices.
        &nbsp;·&nbsp; PY-05: TDS deductions tracked and reconciled via Form 16A.
      </div>
    </div>
  )
}

export default PaymentList
